import { useState, useEffect } from "react";
import type { IThumbnailTemplate } from "../../../../types/thumbnail-template";
import apiService from "../../../../service/Api";
import { useParams, useSearchParams } from "react-router-dom";

type PaginatedResponse<T> = {
  count: number;
  results: T[];
  next: string | null;
  previous: string | null;
};

export const useTemplates = () => {
  const [templates, setTemplates] = useState<IThumbnailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const thumbnailType = searchParams.get("thumbnail_type");

  const fetchTemplates = async () => {
    try {
      const response = await apiService.get<
        PaginatedResponse<IThumbnailTemplate>
      >(`/api/configurations/thumbnails/?thumbnail_type=${thumbnailType}`);
      setTemplates(response.data.results);

      const data = response.data.results || response.data;

      setTemplates(data);
    } catch (err: any) {
      console.error("Failed to fetch templates", err);
      setError("Failed to load templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const addTemplate = async (form: FormData) => {
    try {
      const response = await apiService.post<IThumbnailTemplate>(
        "/api/configurations/thumbnails/",
        form
      );
      setTemplates((prev) => [...prev, response.data]);
      return response.data;
    } catch (err: any) {
      console.error("Failed to add template", err);
      throw err;
    }
  };

  const updateTemplate = async (id: string, form: FormData) => {
    try {
      const response = await apiService.put<IThumbnailTemplate>(
        `/api/configurations/thumbnails/${id}/`,
        form
      );
      setTemplates((prev) =>
        prev.map((t) => (t.id === id ? response.data : t))
      );
      return response.data;
    } catch (err: any) {
      console.error("Failed to update template", err);
      throw err;
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      await apiService.delete(`/api/configurations/thumbnails/${id}/`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err: any) {
      console.error("Failed to delete template", err);
      throw err;
    }
  };

  const getTemplatesByCategory = (category: IThumbnailTemplate["category"]) => {
    const filtered = templates.filter(
      (template) => template.category === category && template.is_active
    );

    return filtered;
  };

  return {
    templates,
    loading,
    error,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplatesByCategory,
  };
};
